import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Tabs,
  Card,
  Table,
  Button,
  Space,
  Modal,
  Drawer,
  Form,
  Input,
  Switch,
  Row,
  Col,
  Statistic,
  message,
} from "antd";
import { connect } from "react-redux";
import _ from "lodash";
import "./super-admin.css";

const { TabPane } = Tabs;
const { TextArea } = Input;

const unwrapList = (payload) => {
  const raw = payload?.data ?? payload;
  const inner = raw?.data !== undefined ? raw.data : raw;
  if (Array.isArray(inner)) return inner;
  if (Array.isArray(inner?.items)) return inner.items;
  if (Array.isArray(inner?.rows)) return inner.rows;
  return [];
};

const unwrapObject = (payload) => {
  const raw = payload?.data ?? payload;
  if (raw?.data !== undefined && !Array.isArray(raw.data)) return raw.data;
  return raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
};

const DataTable = ({ rows, loading }) => {
  const columns = useMemo(() => {
    if (!rows?.length) {
      return [{ title: "—", dataIndex: "empty", key: "empty", render: () => "No rows" }];
    }
    return Object.keys(rows[0]).map((k) => ({
      title: k,
      dataIndex: k,
      key: k,
      ellipsis: true,
      render: (v) =>
        v != null && typeof v === "object" ? (
          <code style={{ fontSize: 11 }}>{JSON.stringify(v)}</code>
        ) : (
          String(v)
        ),
    }));
  }, [rows]);
  return (
    <Table
      size="small"
      rowKey={(r, i) => (r && (r.id ?? r.key)) ?? i}
      loading={loading}
      dataSource={rows}
      columns={columns}
      scroll={{ x: "max-content" }}
    />
  );
};

const LearningOps = (props) => {
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState({});
  const [listRows, setListRows] = useState([]);

  const [genStatus, setGenStatus] = useState("");
  const [genType, setGenType] = useState("");
  const [delStatus, setDelStatus] = useState("");
  const [delChannel, setDelChannel] = useState("");
  const [auditAction, setAuditAction] = useState("");

  const [promptEdit, setPromptEdit] = useState(null);
  const [testPrompt, setTestPrompt] = useState(null);
  const [testJson, setTestJson] = useState("{}");
  const [form] = Form.useForm();

  const [flagJson, setFlagJson] = useState(
    '{\n  "schoolId": "",\n  "teamId": null,\n  "features": {}\n}'
  );

  const [qfForm] = Form.useForm();
  const [grForm] = Form.useForm();
  const [qualityOpen, setQualityOpen] = useState(false);
  const [guardOpen, setGuardOpen] = useState(false);

  const run = useCallback(
    (action, args, onSuccess) => {
      setLoading(true);
      props[action]({
        ...args,
        callback: (ok, data) => {
          setLoading(false);
          if (ok) {
            if (onSuccess) onSuccess(data);
          } else {
            message.error(_.get(data, "error.message") || "Request failed");
          }
        },
      });
    },
    [props]
  );

  const runAsync = useCallback(
    (action, args) =>
      new Promise((resolve, reject) => {
        setLoading(true);
        props[action]({
          ...args,
          callback: (ok, data) => {
            setLoading(false);
            if (ok) resolve(data);
            else reject(data);
          },
        });
      }),
    [props]
  );

  const loadOverview = useCallback(() => {
    run("reportsOverview", {}, (data) => setOverview(unwrapObject(data)));
  }, [run]);

  const loadList = useCallback(
    (action) => {
      run(action, {}, (data) => setListRows(unwrapList(data)));
    },
    [run]
  );

  useEffect(() => {
    if (tab === "overview") loadOverview();
  }, [tab, loadOverview]);

  useEffect(() => {
    if (tab === "prompts") loadList("promptsList");
    if (tab === "deliveries")
      run(
        "deliveriesList",
        {
          params: {
            ...(delStatus && { status: delStatus }),
            ...(delChannel && { channel: delChannel }),
          },
        },
        (data) => setListRows(unwrapList(data))
      );
    if (tab === "quality") loadList("qualityFeedbackList");
    if (tab === "guardrails") loadList("guardrailFailuresList");
    if (tab === "flags") loadList("featureFlagsList");
    if (tab === "audit")
      run(
        "auditLogsList",
        { params: { ...(auditAction && { action: auditAction }) } },
        (data) => setListRows(unwrapList(data))
      );
  }, [tab, loadList, run, delStatus, delChannel, auditAction]);

  const loadGeneration = useCallback(() => {
    run(
      "generationRunsList",
      {
        params: {
          ...(genStatus && { status: genStatus }),
          ...(genType && { generationType: genType }),
        },
      },
      (data) => setListRows(unwrapList(data))
    );
  }, [run, genStatus, genType]);

  useEffect(() => {
    if (tab === "generation") loadGeneration();
  }, [tab, loadGeneration]);

  const overviewCards = useMemo(() => {
    const flat = overview && typeof overview === "object" ? overview : {};
    return Object.entries(flat).filter(([k]) => k !== "success");
  }, [overview]);

  const openPromptEdit = (row) => {
    setPromptEdit(row);
    form.setFieldsValue({
      agentaAppId: row.agentaAppId ?? "",
      agentaPromptVersionId: row.agentaPromptVersionId ?? "",
      environment: row.environment ?? "",
      enabled: row.enabled ?? false,
    });
  };

  const submitPromptPatch = () => {
    form.validateFields().then((values) => {
      run(
        "promptPatch",
        {
          params: { promptType: promptEdit.promptType ?? promptEdit.type ?? promptEdit.key },
          body: values,
        },
        () => {
          message.success("Prompt updated");
          setPromptEdit(null);
          loadList("promptsList");
        }
      );
    });
  };

  const submitPromptTest = () => {
    let body = {};
    try {
      body = JSON.parse(testJson || "{}");
    } catch (e) {
      message.error("Invalid JSON");
      return;
    }
    run(
      "promptTest",
      {
        params: {
          promptType: testPrompt.promptType ?? testPrompt.type ?? testPrompt.key,
        },
        body,
      },
      (data) => {
        Modal.info({
          title: "Test result",
          width: 720,
          content: (
            <pre style={{ maxHeight: 400, overflow: "auto" }}>
              {JSON.stringify(unwrapObject(data), null, 2)}
            </pre>
          ),
        });
      }
    );
  };

  const genColumns = useMemo(() => {
    const base = listRows.length ? Object.keys(listRows[0]) : [];
    const cols = base.map((k) => ({
      title: k,
      dataIndex: k,
      key: k,
      ellipsis: true,
      render: (v) =>
        v != null && typeof v === "object" ? (
          <code style={{ fontSize: 11 }}>{JSON.stringify(v)}</code>
        ) : (
          String(v)
        ),
    }));
    if (tab === "generation") {
      cols.push({
        title: "Actions",
        key: "actions",
        fixed: "right",
        render: (_, row) => (
          <Button
            size="small"
            onClick={() =>
              Modal.confirm({
                title: "Retry this run?",
                onOk: () =>
                  runAsync("generationRunRetry", {
                    params: { id: row.id ?? row.runId },
                  }).then(() => {
                    message.success("Retry queued");
                    loadGeneration();
                  }),
              })
            }
          >
            Retry
          </Button>
        ),
      });
    }
    return cols;
  }, [listRows, tab, runAsync, loadGeneration]);

  const promptColumns = useMemo(() => {
    const cols =
      listRows.length && listRows[0]
        ? Object.keys(listRows[0]).map((k) => ({
            title: k,
            dataIndex: k,
            key: k,
            ellipsis: true,
          }))
        : [];
    cols.push({
      title: "Actions",
      key: "act",
      fixed: "right",
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => openPromptEdit(row)}>
            Edit
          </Button>
          <Button
            size="small"
            onClick={() => {
              setTestPrompt(row);
              setTestJson("{}");
            }}
          >
            Test
          </Button>
        </Space>
      ),
    });
    return cols;
  }, [listRows]);

  return (
    <div className="p-4">
      <Card title="Learning ops" bordered={false}>
        <Tabs activeKey={tab} onChange={setTab}>
          <TabPane tab="Overview" key="overview">
            <Space style={{ marginBottom: 16 }}>
              <Button onClick={loadOverview}>Refresh</Button>
            </Space>
            <Row gutter={[16, 16]}>
              {overviewCards.map(([k, v]) => (
                <Col xs={24} sm={12} md={8} key={k}>
                  <Card size="small" title={k}>
                    {typeof v === "number" ? (
                      <Statistic value={v} />
                    ) : (
                      <pre style={{ margin: 0, fontSize: 12, whiteSpace: "pre-wrap" }}>
                        {typeof v === "object" ? JSON.stringify(v, null, 2) : String(v)}
                      </pre>
                    )}
                  </Card>
                </Col>
              ))}
            </Row>
            {!overviewCards.length && (
              <p className="text-gray-500">No overview metrics (check API response shape).</p>
            )}
          </TabPane>

          <TabPane tab="Prompts" key="prompts">
            <Space style={{ marginBottom: 16 }}>
              <Button onClick={() => loadList("promptsList")}>Refresh</Button>
            </Space>
            <Table
              size="small"
              loading={loading}
              rowKey={(r, i) => r.promptType ?? r.type ?? r.key ?? i}
              dataSource={listRows}
              columns={promptColumns}
              scroll={{ x: "max-content" }}
            />
          </TabPane>

          <TabPane tab="Generation" key="generation">
            <Space wrap style={{ marginBottom: 16 }}>
              <Input
                placeholder="status"
                style={{ width: 140 }}
                value={genStatus}
                onChange={(e) => setGenStatus(e.target.value)}
                allowClear
              />
              <Input
                placeholder="generationType"
                style={{ width: 160 }}
                value={genType}
                onChange={(e) => setGenType(e.target.value)}
                allowClear
              />
              <Button type="primary" onClick={loadGeneration}>
                Load
              </Button>
            </Space>
            <Table
              size="small"
              loading={loading}
              rowKey={(r, i) => r.id ?? i}
              dataSource={listRows}
              columns={genColumns}
              scroll={{ x: "max-content" }}
            />
          </TabPane>

          <TabPane tab="Deliveries" key="deliveries">
            <Space wrap style={{ marginBottom: 16 }}>
              <Input
                placeholder="status"
                style={{ width: 140 }}
                value={delStatus}
                onChange={(e) => setDelStatus(e.target.value)}
                allowClear
              />
              <Input
                placeholder="channel"
                style={{ width: 140 }}
                value={delChannel}
                onChange={(e) => setDelChannel(e.target.value)}
                allowClear
              />
              <Button
                type="primary"
                onClick={() =>
                  run(
                    "deliveriesList",
                    {
                      params: {
                        ...(delStatus && { status: delStatus }),
                        ...(delChannel && { channel: delChannel }),
                      },
                    },
                    (data) => setListRows(unwrapList(data))
                  )
                }
              >
                Load
              </Button>
            </Space>
            <DataTable rows={listRows} loading={loading} />
          </TabPane>

          <TabPane tab="Quality" key="quality">
            <Space style={{ marginBottom: 16 }}>
              <Button onClick={() => loadList("qualityFeedbackList")}>Refresh</Button>
              <Button type="primary" onClick={() => setQualityOpen(true)}>
                Submit feedback
              </Button>
            </Space>
            <DataTable rows={listRows} loading={loading} />
          </TabPane>

          <TabPane tab="Guardrails" key="guardrails">
            <Space style={{ marginBottom: 16 }}>
              <Button onClick={() => loadList("guardrailFailuresList")}>Refresh</Button>
              <Button type="primary" onClick={() => setGuardOpen(true)}>
                Record failure (test)
              </Button>
            </Space>
            <DataTable rows={listRows} loading={loading} />
          </TabPane>

          <TabPane tab="Flags" key="flags">
            <Space direction="vertical" style={{ width: "100%" }} size="middle">
              <Space>
                <Button onClick={() => loadList("featureFlagsList")}>Refresh list</Button>
              </Space>
              <Card size="small" title="PATCH feature flags (JSON)">
                <TextArea rows={8} value={flagJson} onChange={(e) => setFlagJson(e.target.value)} />
                <Button
                  style={{ marginTop: 8 }}
                  type="primary"
                  onClick={() => {
                    let body;
                    try {
                      body = JSON.parse(flagJson);
                    } catch (e) {
                      message.error("Invalid JSON");
                      return;
                    }
                    run("featureFlagsPatch", { body }, () => {
                      message.success("Updated");
                      loadList("featureFlagsList");
                    });
                  }}
                >
                  Apply patch
                </Button>
              </Card>
              <DataTable rows={listRows} loading={loading} />
            </Space>
          </TabPane>

          <TabPane tab="Audit" key="audit">
            <Space style={{ marginBottom: 16 }}>
              <Input
                placeholder="action filter"
                style={{ width: 200 }}
                value={auditAction}
                onChange={(e) => setAuditAction(e.target.value)}
                allowClear
              />
              <Button
                type="primary"
                onClick={() =>
                  run(
                    "auditLogsList",
                    { params: { ...(auditAction && { action: auditAction }) } },
                    (data) => setListRows(unwrapList(data))
                  )
                }
              >
                Load
              </Button>
            </Space>
            <DataTable rows={listRows} loading={loading} />
          </TabPane>

          <TabPane tab="Patterns" key="patterns">
            <Button
              type="primary"
              danger
              onClick={() =>
                Modal.confirm({
                  title: "Rebuild learning patterns?",
                  onOk: () =>
                    runAsync("patternsRebuild", {}).then(() =>
                      message.success("Rebuild triggered")
                    ),
                })
              }
            >
              Rebuild patterns
            </Button>
          </TabPane>
        </Tabs>
      </Card>

      <Drawer
        title="Edit prompt config"
        open={!!promptEdit}
        onClose={() => setPromptEdit(null)}
        width={400}
        footer={
          <Space>
            <Button onClick={() => setPromptEdit(null)}>Cancel</Button>
            <Button type="primary" onClick={submitPromptPatch}>
              Save
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="agentaAppId" label="agentaAppId">
            <Input />
          </Form.Item>
          <Form.Item name="agentaPromptVersionId" label="agentaPromptVersionId">
            <Input />
          </Form.Item>
          <Form.Item name="environment" label="environment">
            <Input />
          </Form.Item>
          <Form.Item name="enabled" label="enabled" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Drawer>

      <Modal
        title="Test prompt"
        open={!!testPrompt}
        onCancel={() => setTestPrompt(null)}
        onOk={submitPromptTest}
        width={600}
      >
        <p className="text-sm text-gray-500 mb-2">PromptTestRequest JSON body</p>
        <TextArea rows={10} value={testJson} onChange={(e) => setTestJson(e.target.value)} />
      </Modal>

      <Modal
        title="Quality feedback"
        open={qualityOpen}
        onCancel={() => setQualityOpen(false)}
        onOk={() =>
          qfForm.validateFields().then((v) =>
            run("qualityFeedbackCreate", { body: v }, () => {
              message.success("Submitted");
              setQualityOpen(false);
              loadList("qualityFeedbackList");
            })
          )
        }
      >
        <Form form={qfForm} layout="vertical">
          <Form.Item name="contentType" label="contentType" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="contentId" label="contentId" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="rating" label="rating" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="notes" label="notes">
            <TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Guardrail failure"
        open={guardOpen}
        onCancel={() => setGuardOpen(false)}
        onOk={() =>
          grForm.validateFields().then((v) => {
            let details = v.detailsJson;
            try {
              details = v.detailsJson ? JSON.parse(v.detailsJson) : undefined;
            } catch (e) {
              message.error("details JSON invalid");
              return Promise.reject();
            }
            const { detailsJson, ...rest } = v;
            run(
              "guardrailFailureCreate",
              { body: { ...rest, ...(details !== undefined && { details }) } },
              () => {
                message.success("Recorded");
                setGuardOpen(false);
                loadList("guardrailFailuresList");
              }
            );
          })
        }
      >
        <Form form={grForm} layout="vertical">
          <Form.Item name="contentType" label="contentType" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="contentId" label="contentId" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="reason" label="reason" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="detailsJson" label="details (JSON)">
            <TextArea rows={4} placeholder="{}" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

const mapDispatch = (dispatch) => ({
  reportsOverview: (d) => dispatch(global.redux.action.learning_admin.reportsOverview(d)),
  promptsList: (d) => dispatch(global.redux.action.learning_admin.promptsList(d)),
  promptPatch: (d) => dispatch(global.redux.action.learning_admin.promptPatch(d)),
  promptTest: (d) => dispatch(global.redux.action.learning_admin.promptTest(d)),
  generationRunsList: (d) => dispatch(global.redux.action.learning_admin.generationRunsList(d)),
  generationRunRetry: (d) => dispatch(global.redux.action.learning_admin.generationRunRetry(d)),
  deliveriesList: (d) => dispatch(global.redux.action.learning_admin.deliveriesList(d)),
  qualityFeedbackList: (d) => dispatch(global.redux.action.learning_admin.qualityFeedbackList(d)),
  qualityFeedbackCreate: (d) => dispatch(global.redux.action.learning_admin.qualityFeedbackCreate(d)),
  guardrailFailuresList: (d) => dispatch(global.redux.action.learning_admin.guardrailFailuresList(d)),
  guardrailFailureCreate: (d) =>
    dispatch(global.redux.action.learning_admin.guardrailFailureCreate(d)),
  featureFlagsList: (d) => dispatch(global.redux.action.learning_admin.featureFlagsList(d)),
  featureFlagsPatch: (d) => dispatch(global.redux.action.learning_admin.featureFlagsPatch(d)),
  auditLogsList: (d) => dispatch(global.redux.action.learning_admin.auditLogsList(d)),
  patternsRebuild: (d) => dispatch(global.redux.action.learning_admin.patternsRebuild(d)),
});

export default connect(null, mapDispatch)(LearningOps);
